// Wake-word cue: distinct, quick, rising arpeggio (C5, E5, G5)
export function playWakeWordCue() {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  blip(ctx, { freq: 523, startAt: t,        durMs: 60, type: "triangle", peak: 0.22 }); // C5
  blip(ctx, { freq: 659, startAt: t + 0.06, durMs: 60, type: "triangle", peak: 0.22 }); // E5
  blip(ctx, { freq: 784, startAt: t + 0.12, durMs: 90, type: "triangle", peak: 0.24 }); // G5
}

// Intent-confirmation cue: soft, high, single chime (B5)
export function playIntentConfirmationCue() {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  blip(ctx, { freq: 988, startAt: t, durMs: 120, type: "sine", peak: 0.19 }); // B5
}
// Lightweight Web Audio cues for app events. Distinct tones per event so the
// user can identify them by ear without looking at the screen.
//
// Cues:
//   recordStart   — single rising chirp (A4 -> E5)
//   recordStop    — descending two-tone (E5 -> A4)
//   wifiConnected — bright triple-blip arpeggio (E5, G5, B5)

let ctxRef = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!ctxRef) {
    try { ctxRef = new Ctor(); } catch (_) { ctxRef = null; }
  }
  if (ctxRef && ctxRef.state === "suspended") {
    // best-effort resume; browsers require a user gesture which we usually have
    try { ctxRef.resume(); } catch (_) {}
  }
  return ctxRef;
}

function blip(ctx, { freq, startAt, durMs, type = "sine", peak = 0.25 }) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  // fast attack / exponential release for a clean "blip"
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durMs / 1000);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + durMs / 1000 + 0.02);
}

function chirp(ctx, { from, to, startAt, durMs, type = "triangle", peak = 0.25 }) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(from, startAt);
  osc.frequency.exponentialRampToValueAtTime(to, startAt + durMs / 1000);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durMs / 1000);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + durMs / 1000 + 0.02);
}

export function playRecordStartCue() {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  // single rising chirp ~180ms: A4 (440) -> E5 (659)
  chirp(ctx, { from: 440, to: 659, startAt: t, durMs: 180, type: "triangle", peak: 0.28 });
}

export function playRecordStopCue() {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  // descending two-tone: E5 then A4
  blip(ctx, { freq: 659, startAt: t, durMs: 110, type: "triangle", peak: 0.26 });
  blip(ctx, { freq: 440, startAt: t + 0.12, durMs: 160, type: "triangle", peak: 0.26 });
}

// Clip-limit warning: an urgent, attention-grabbing triple high beep played
// ~30s before a GoPro recording auto-stops, prompting the user to look at their
// phone. Deliberately brighter/more insistent than the start/stop cues.
export function playRecordLimitWarningCue() {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  const freq = 988; // B5, bright and cutting
  for (let i = 0; i < 3; i++) {
    blip(ctx, { freq, startAt: t + i * 0.22, durMs: 150, type: "square", peak: 0.3 });
  }
}

export function playWifiConnectedCue() {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  // bright triple-blip arpeggio: E5, G5, B5
  blip(ctx, { freq: 659, startAt: t,         durMs: 90, type: "sine", peak: 0.22 });
  blip(ctx, { freq: 784, startAt: t + 0.08,  durMs: 90, type: "sine", peak: 0.22 });
  blip(ctx, { freq: 988, startAt: t + 0.16,  durMs: 130, type: "sine", peak: 0.24 });
}

// GoPro-disconnect alert: an urgent, repeating series of identical low beeps —
// deliberately unlike the record start (rising chirp) and stop (descending
// two-tone) cues so a dropped camera link is unmistakable by ear.
export function playGoProDisconnectCue() {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  const freq = 360; // low, alarm-like
  for (let i = 0; i < 4; i++) {
    blip(ctx, { freq, startAt: t + i * 0.18, durMs: 110, type: "square", peak: 0.24 });
  }
}

// --- Spoken voice announcements (Web Speech API) ---------------------------

// Pick a female-sounding voice from the system TTS engine. Voice availability
// varies by device/OS, so fall back gracefully to the default voice.
function pickFemaleVoice(synth) {
  let voices = [];
  try { voices = synth.getVoices() || []; } catch (_) { voices = []; }
  if (!voices.length) return null;
  const enVoices = voices.filter((v) => /^en/i.test(v.lang || ""));
  const pool = enVoices.length ? enVoices : voices;
  // Prefer voices that explicitly identify as female or are commonly female.
  const femaleHints = /female|samantha|karen|moira|tessa|fiona|victoria|susan|zira|hazel|salli|joanna|amy|emma|serena|allison|ava|#female/i;
  return (
    pool.find((v) => femaleHints.test(v.name || "")) ||
    pool.find((v) => femaleHints.test(`${v.voiceURI || ""}`)) ||
    pool[0]
  );
}

// Speak a short phrase in a female voice. No-op if speech synthesis is
// unavailable. Cancels any in-flight utterance so announcements stay snappy.
export function speak(text) {
  if (typeof window === "undefined") return;
  const synth = window.speechSynthesis;
  if (!synth || typeof window.SpeechSynthesisUtterance !== "function") return;
  try {
    const say = () => {
      try {
        synth.cancel();
        const u = new window.SpeechSynthesisUtterance(text);
        const voice = pickFemaleVoice(synth);
        if (voice) u.voice = voice;
        u.lang = (voice && voice.lang) || "en-US";
        u.rate = 1.0;
        u.pitch = 1.1; // slightly higher for a clearer female timbre
        u.volume = 1.0;
        synth.speak(u);
      } catch (_) {}
    };
    // Voices load asynchronously on some platforms; retry once they're ready.
    const voices = synth.getVoices ? synth.getVoices() : [];
    if (!voices || !voices.length) {
      const once = () => { synth.onvoiceschanged = null; say(); };
      synth.onvoiceschanged = once;
      // Fallback in case the event never fires.
      setTimeout(() => { if (synth.onvoiceschanged === once) { synth.onvoiceschanged = null; say(); } }, 300);
    } else {
      say();
    }
  } catch (_) {}
}

export function speakGoProConnected() {
  speak("GoPro connected");
}

export function speakGoProDisconnected() {
  speak("GoPro disconnected");
}

// Voice command cues (Phase 2a)
export function playWakeCue() {
  // Short two-note "listening" chime: C5 -> G5
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  blip(ctx, { freq: 523, startAt: t,        durMs: 70, type: "sine", peak: 0.22 });
  blip(ctx, { freq: 784, startAt: t + 0.07, durMs: 90, type: "sine", peak: 0.22 });
}

export function playIntentOkCue() {
  // Soft confirmation blip: G5
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  blip(ctx, { freq: 784, startAt: t, durMs: 80, type: "sine", peak: 0.2 });
}

export function playIntentUnknownCue() {
  // Low descending pair: D4 -> A3
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + 0.01;
  blip(ctx, { freq: 294, startAt: t,        durMs: 90, type: "triangle", peak: 0.2 });
  blip(ctx, { freq: 220, startAt: t + 0.10, durMs: 130, type: "triangle", peak: 0.2 });
}
